import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrgInvitations, inviteMember, saGetOrganizations } from '../../api/api';
import StyledSelect from '../../components/StyledSelect';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'admin' },
  { value: 'finance_manager', label: 'finance manager' },
  { value: 'accountant', label: 'accountant' },
  { value: 'project_manager', label: 'project manager' },
  { value: 'employee', label: 'employee' },
  { value: 'viewer', label: 'viewer' },
];

export default function SuperAdminOnboarding() {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('employee');
  const [invitations, setInvitations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedOrg = useMemo(
    () => organizations.find((row) => String(row.id) === String(selectedOrgId)) || null,
    [organizations, selectedOrgId]
  );

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'Select organization' },
      ...organizations.map((row) => ({ value: String(row.id), label: `${row.name} (${row.code || row.slug || row.id})` })),
    ],
    [organizations]
  );

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    setError('');
    try {
      const res = await saGetOrganizations({ page: 1, limit: 200 });
      const rows = res?.data?.data?.organizations || [];
      setOrganizations(rows);
      if (rows.length > 0 && !selectedOrgId) {
        setSelectedOrgId(String(rows[0].id));
      }
    } catch (e) {
      setOrganizations([]);
      setError(e?.response?.data?.message || 'Failed to load organizations.');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadInvitations = async (orgId) => {
    if (!orgId) {
      setInvitations([]);
      return;
    }
    setLoadingInvites(true);
    setError('');
    try {
      const res = await getOrgInvitations(orgId);
      setInvitations(res?.data?.data?.invitations || []);
    } catch (e) {
      setInvitations([]);
      setError(e?.response?.data?.message || 'Failed to load invitations for this organization.');
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (!selectedOrgId) return;
    loadInvitations(selectedOrgId);
  }, [selectedOrgId]);

  const onInvite = async (e) => {
    e.preventDefault();
    if (!selectedOrgId) {
      setError('Please select an organization.');
      return;
    }
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }

    setSendingInvite(true);
    setError('');
    setSuccess('');

    try {
      const res = await inviteMember(selectedOrgId, { email: email.trim(), org_role: role });
      const inviteCode = res?.data?.data?.invite_code;
      setSuccess(inviteCode ? `Invitation created. Invite code: ${inviteCode}` : 'Invitation created successfully.');
      setEmail('');
      await loadInvitations(selectedOrgId);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create invitation.');
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <div className="space-y-5">
      <section className="superadmin-glass rounded-2xl p-5">
        <h1 className="superadmin-title text-3xl lg:text-4xl font-bold">Onboarding Center</h1>
        <p className="text-sm text-slate-500 mt-1">
          Reuse the same EMS registration and invitation architecture from one Super Admin workspace.
        </p>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="superadmin-glass rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Shared Flow</p>
          <h2 className="text-lg font-bold text-slate-900 mt-1">Create New Organization</h2>
          <p className="text-sm text-slate-500 mt-2">Open the existing registration flow without duplicating backend logic.</p>
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="mt-4 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Open Register Flow
          </button>
        </div>

        <div className="superadmin-glass rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Shared Flow</p>
          <h2 className="text-lg font-bold text-slate-900 mt-1">Join Existing Organization</h2>
          <p className="text-sm text-slate-500 mt-2">Use the current invitation token join screen used by all users.</p>
          <button
            type="button"
            onClick={() => navigate('/join')}
            className="mt-4 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Open Join Flow
          </button>
        </div>
      </section>

      <section className="superadmin-glass rounded-2xl p-5 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Invitation</p>
          <h2 className="text-lg font-bold text-slate-900 mt-1">Invite Member by Organization + Role + Email</h2>
          <p className="text-sm text-slate-500 mt-1">
            Uses the same organization invitation endpoint and table. No alternate invitation pipeline is introduced.
          </p>
        </div>

        {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</div> : null}

        <form onSubmit={onInvite} className="grid grid-cols-1 xl:grid-cols-4 gap-3 items-end">
          <div className="xl:col-span-2">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Organization</label>
            <StyledSelect
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              options={orgOptions}
              placeholder={loadingOrgs ? 'Loading organizations...' : 'Select organization'}
              searchable
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Role</label>
            <StyledSelect
              value={role}
              onChange={setRole}
              options={ROLE_OPTIONS}
              placeholder="Select role"
              searchable
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="member@example.com"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              required
            />
          </div>
          <div className="xl:col-span-4">
            <button
              type="submit"
              disabled={sendingInvite || !selectedOrgId}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: '#c44569' }}
            >
              {sendingInvite ? 'Sending Invitation...' : 'Create Invitation'}
            </button>
          </div>
        </form>

        <div className="pt-2">
          <p className="text-sm font-semibold text-slate-700">
            Active organization: <span className="text-slate-900">{selectedOrg?.name || 'None selected'}</span>
          </p>
        </div>

        <div className="overflow-auto rounded-xl border border-slate-100 bg-white">
          {loadingInvites ? (
            <p className="px-4 py-4 text-sm text-slate-500">Loading invitations...</p>
          ) : invitations.length === 0 ? (
            <p className="px-4 py-4 text-sm text-slate-500">No invitations found for this organization.</p>
          ) : (
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 px-3">Email</th>
                  <th className="px-3">Role</th>
                  <th className="px-3">Status</th>
                  <th className="px-3">Invite Code</th>
                  <th className="px-3">Expires</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((row) => (
                  <tr key={row.id} className="border-b border-slate-50">
                    <td className="py-2 px-3">{row.email}</td>
                    <td className="px-3">{row.org_role || 'employee'}</td>
                    <td className="px-3">{row.status || '-'}</td>
                    <td className="px-3">{row.invite_code || 'n/a'}</td>
                    <td className="px-3">{row.expires_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
